import {
  BackButton,
  Banner,
  getSvgByName,
  GutXAxisBody,
  Icon,
  resolveGutXAxisSystems,
  Typography,
} from '@repo/ui';
import { ScrollHandler } from '@repo/utils';
import c from 'classnames';
import { BetaTag, ContainerSizes, PageContainer } from 'components';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import useHashScroll from 'hooks/useHashScroll';
import useObserve from 'hooks/useObserve';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useFeature from 'services/features/useFeature';
import useOrgans from 'services/organs/useOrgans';
import { organAnchorId } from 'views/Results/utils/organs';
import { useConditionsEducationalModal } from '../ConditionsEducationalModal/context';
import { ConditionsInfoModal } from '../ConditionsInfoModal';
import ConditionMarkersSection from './ConditionMarkersSection';
import { useConditionMarkers, useOrganSelect } from './hooks';
import styles from './NewConditionMetricsList.module.css';
import OrganSection from './OrganSection';
import OrganSelector from './OrganSelector';

const scrollHandler = ScrollHandler.instance;

export default function NewConditionMetricsList() {
  const navigate = useNavigate();
  const { currentKitId, currentKit, currentTinyAccount } =
    useCurrentKitAndTinyAccount();
  const { organs } = useOrgans(currentKitId);
  const markers = useConditionMarkers(currentKitId);
  const { trackEvent } = useAnalytics();
  const handleOrganSelect = useOrganSelect();

  const { isFeatureEnabled: isHAEMEnabled } = useFeature(
    'health_associations_educational_modal',
  );
  const { openReview } = useConditionsEducationalModal();

  const age = currentKit?.age_at_sampling ?? 0;
  const pregnant = !!currentKit?.pregnant;
  const sex = currentTinyAccount?.sex ?? 'M';

  // Resolved here too, so the sections list exactly the organs the map draws.
  const { listedSystems, drawnCodes } = useMemo(
    () => resolveGutXAxisSystems({ organs, age, pregnant, sex }),
    [organs, age, pregnant, sex],
  );

  // Waits on the sections, which only exist once the organs have resolved.
  useHashScroll({ elementsWithId: listedSystems });

  const anchorIds = useMemo(
    () => listedSystems.map(({ organ }) => organAnchorId(organ.lookup_code)),
    [listedSystems],
  );
  const visibleIds = useObserve(anchorIds, {
    rootMargin: '-100px 0px -65% 0px',
  });
  const activeId = anchorIds.find(id => visibleIds.has(id)) ?? anchorIds[0];

  // `trackEvent` is rebuilt when the kit resolves, which would otherwise report
  // the same visit twice. The ref keeps a switch of kit reporting a new one.
  const viewedKitRef = useRef<string>();

  useEffect(() => {
    if (!currentKitId || viewedKitRef.current === currentKitId) return;
    viewedKitRef.current = currentKitId;
    trackEvent('Conditions Home Viewed');
  }, [currentKitId, trackEvent]);

  const goBack = () => {
    scrollHandler.setScrollRestoration();
    navigate(-1);
  };

  return (
    <PageContainer size={ContainerSizes.LG} className={styles.pageContainer}>
      {!isHAEMEnabled ? <ConditionsInfoModal /> : null}
      {/* The layout's own floating one is suppressed for this route, so it
          cannot hover over the organ selector. */}
      <BackButton
        goBack={goBack}
        className={c(styles.backButton, 'hide-on-desktop')}
      />
      <Breadcrumb overrides={{ conditions: 'Gut body connections' }} />
      <div className={styles.textsContainer}>
        <div className={styles.titleRow}>
          <Typography variant='heading-l'>Gut body connections</Typography>
          {isHAEMEnabled ? (
            <button
              type='button'
              className={styles.infoButton}
              onClick={openReview}
              aria-label='Learn more about gut body connections'
            >
              <Icon name='infoCircle' size='m' color='grey' />
            </button>
          ) : null}
          <BetaTag />
        </div>
        <Typography variant='body-s' color='grey'>
          Discover connections between your health and your microbiome report.
        </Typography>
      </div>
      <Banner
        color='purple'
        title='This test does not diagnose any health conditions.'
        content='This is a wellness test, not a diagnostic test. The results show microbiome associations with health conditions, not causes. Multiple factors including genetics, diet, and lifestyle can contribute to risk.'
        icon={getSvgByName('infoCircle')}
        className={styles.banner}
        iconContainerClassName={styles.bannerIconContainer}
        iconClassName={styles.bannerIcon}
        variant='full'
        size='sm'
      />
      <OrganSelector
        organs={listedSystems.map(({ organ }) => organ)}
        activeId={activeId}
        age={age}
        pregnant={pregnant}
        sex={sex}
        drawnCodes={drawnCodes}
      />
      <div className={styles.content}>
        <div className={styles.bodyContainer}>
          <GutXAxisBody
            age={age}
            pregnant={pregnant}
            organs={organs}
            sex={sex}
            hoverVariant='pill'
            onOrganSelect={handleOrganSelect}
          />
        </div>
        <div className={styles.organSectionsContainer}>
          {listedSystems.map(({ organ }) => (
            <OrganSection
              key={organ.id}
              organ={organ}
              age={age}
              pregnant={pregnant}
              sex={sex}
              drawnCodes={drawnCodes}
            />
          ))}
          <ConditionMarkersSection markers={markers} />
        </div>
      </div>
    </PageContainer>
  );
}
